import type { SettingsUmbBlockGridCompositionElementModel } from "#/_generated/umbraco/delivery-api/models";

type LayoutSettings = {
	textColor?: string | null;
	backgroundColor?: string | null;
	backgroundColorOpacity?: number | string | null;
	heightMinimum?: number | string | null;
	paddingTop?: number | string | null;
	paddingBottom?: number | string | null;
	containerWidth?: string | null;
	containerAlignment?: string | null;
	backgroundMediaAnimationType?: string | null;
};

type BlockLayoutCss = {
	textColor?: string;
	backgroundColor?: string;
	backgroundColorOpacity?: string;
	minHeight?: string;
	paddingTop?: string;
	paddingBottom?: string;
	containerWidth?: string;
	containerAlignment?: string;
	bgMediaAnimationType?: string | null;
};

const textIntentClasses = new Map<string, string>([
	["primary", "text-primary"],
	["secondary", "text-secondary"],
	["light", "text-light"],
	["dark", "text-dark"],
	["white", "text-white"],
	["black", "text-black"],
	["success", "text-success"],
	["warning", "text-warning"],
	["danger", "text-danger"],
	["info", "text-info"],
	["none", "text-text-base"],
]);

const bgIntentClasses = new Map<string, string>([
	["primary", "bg-primary"],
	["secondary", "bg-secondary"],
	["light", "bg-light"],
	["dark", "bg-dark"],
	["white", "bg-white"],
	["black", "bg-black"],
	["success", "bg-success"],
	["warning", "bg-warning"],
	["danger", "bg-danger"],
	["info", "bg-info"],
	["none", "bg-transparent"],
]);

export function mapBlockLayoutCssFromBlockGrid(
	settings: SettingsUmbBlockGridCompositionElementModel,
) {
	const layoutSettings: LayoutSettings = {
		textColor: settings.properties?.textColor,
		backgroundColor: settings.properties?.backgroundColor,
		heightMinimum: settings.properties?.heightMinimum,
		paddingTop: settings.properties?.paddingTop,
		paddingBottom: settings.properties?.paddingBottom,
		containerWidth: settings.properties?.containerWidth,
		containerAlignment: settings.properties?.containerAlignment,
		backgroundMediaAnimationType:
			settings.properties?.backgroundMediaAnimationType,
	};

	return mapBlockLayoutCss(layoutSettings);
}
export function mapBlockLayoutCss(
	layoutSettings?: LayoutSettings | null,
): BlockLayoutCss {
	return {
		textColor:
			layoutSettings?.textColor && layoutSettings.textColor.trim() !== ""
				? textIntentClasses.get(layoutSettings.textColor)
				: "",
		backgroundColor:
			layoutSettings?.backgroundColor &&
			layoutSettings.backgroundColor.trim() !== "" &&
			!layoutSettings.backgroundColor.startsWith("#")
				? bgIntentClasses.get(layoutSettings.backgroundColor)
				: "",
		backgroundColorOpacity: (() => {
			const opacity = normalizeBackgroundColorOpacity(
				layoutSettings?.backgroundColorOpacity,
			);
			return opacity
				? `--umb-bg-opacity: ${opacity}; --bs-bg-opacity: ${opacity};`
				: "";
		})(),
		minHeight:
			layoutSettings?.heightMinimum != null
				? `min-height: ${layoutSettings.heightMinimum}vh; display: flex; align-items: center;`
				: "",
		paddingTop:
			layoutSettings?.paddingTop != null
				? `padding-top: calc(0.33333334vh * ${layoutSettings.paddingTop});`
				: "",
		paddingBottom:
			layoutSettings?.paddingBottom != null
				? `padding-bottom: calc(0.33333334vh * ${layoutSettings.paddingBottom});`
				: "",
		containerWidth: (() => {
			switch (layoutSettings?.containerWidth) {
				case "alwaysContained":
					return "container ";
				case "fluidToSM":
					return "mx-auto max-w-screen-sm";
				case "fluidToMD":
					return "mx-auto max-w-screen-md";
				case "fluidToLG":
					return "mx-auto max-w-screen-lg";
				case "fluidToXL":
					return "mx-auto max-w-screen-xl";
				case "fluidToXXL":
					return "mx-auto max-w-screen-2xl";
				case "alwaysFluid":
					return "w-full max-w-none";
				default:
					return "container ";
			}
		})(),
		containerAlignment: (() => {
			switch (layoutSettings?.containerAlignment) {
				case "start":
					return "container-start";
				case "center":
					return "";
				case "end":
					return "container-end";
				default:
					return "";
			}
		})(),
		bgMediaAnimationType: layoutSettings?.backgroundMediaAnimationType ?? null,
	};
}

export function styleStringToObject(styleString: string) {
	return Object.fromEntries(
		styleString
			.split(";")
			.filter(Boolean)
			.map((rule) => {
				const [key, value] = rule.split(":");
				const trimmedKey = key.trim();
				const camelKey = trimmedKey.startsWith("--")
					? trimmedKey
					: trimmedKey.replace(/-([a-z])/g, (_, char) => char.toUpperCase());

				return [camelKey, value.trim()];
			}),
	);
}

function normalizeBackgroundColorOpacity(
	opacity?: number | string | null,
): string | undefined {
	if (opacity == null) return "1";
	if (typeof opacity === "number") {
		return normalizeOpacityValue(opacity);
	}

	const trimmed = opacity.trim();
	if (trimmed === "") return "1";
	if (trimmed.endsWith("%")) {
		return normalizeOpacityValue(parseFloat(trimmed.slice(0, -1)));
	}

	return normalizeOpacityValue(parseFloat(trimmed));
}

function normalizeOpacityValue(value: number) {
	if (Number.isNaN(value)) return undefined;
	const normalized = value > 1 ? value / 100 : value;
	return Math.min(1, Math.max(0, normalized)).toString();
}
